<!-- src/components/AdminLogsInfinite.vue -->
<template>
  <v-card class="logs-infinite" elevation="0">
    <!-- Header now scrolls with the page -->
    <div class="topbar">
      <div class="right">
        <v-btn small depressed :loading="refreshing" @click="refresh">
          <v-icon left>mdi-reload</v-icon> Refresh
        </v-btn>
      </div>
    </div>

    <!-- The whole page scrolls; no inner scroller -->
    <transition-group name="fade-slide" tag="div" class="list">
      <div
        v-for="item in items"
        :key="item.id"
        class="log-row"
        @click="openDetails(item)"
      >
        <div class="time mono">{{ formatDate(item.createdAt) }}</div>

        <div class="main">
          <div class="from">{{ item.from }}</div>
          <div class="msg">{{ truncate(item.message, 240) }}</div>
        </div>

        <div class="meta">
          <v-chip
            small
            outlined
            label
            class="level-chip"
            :class="levelClass(item.level)"
          >
            <v-icon left small>{{ levelIcon(item.level) }}</v-icon>
            {{ item.level || 'INFO' }}
          </v-chip>
        </div>
      </div>
    </transition-group>

    <!-- Loading | End markers -->
    <div v-if="loadingMore" class="loading-more">
      <v-progress-circular indeterminate size="20" />
      <span class="ml-2">Loading more…</span>
    </div>
    <div v-if="noMore && items.length" class="end-marker">
      <v-icon small>mdi-check</v-icon>
      <span class="ml-1">You’ve reached the end</span>
    </div>
    <div v-if="!loading && !items.length" class="empty">
      <v-icon large>mdi-clipboard-text-outline</v-icon>
      <div>No logs yet</div>
    </div>

    <!-- Sentinel at the very bottom of the page flow -->
    <div ref="sentinel" class="sentinel"></div>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="760px">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-clipboard-text</v-icon> Log details
          <v-spacer />
          <v-btn icon @click="detailsDialog=false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div class="details-grid">
            <div><span class="label">ID</span><span class="value mono">{{ details?.id }}</span></div>
            <div><span class="label">Created</span><span class="value mono">{{ formatDate(details?.createdAt) }}</span></div>
            <div><span class="label">Level</span>
              <span class="value">
                <v-chip x-small outlined :class="levelClass(details?.level)">
                  {{ details?.level || 'INFO' }}
                </v-chip>
              </span>
            </div>
            <div class="wide"><span class="label">From</span><span class="value">{{ details?.from }}</span></div>
            <div class="wide">
              <span class="label">Message</span>
              <pre class="code">{{ details?.message }}</pre>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="copy(details?.message)">
            <v-icon left>mdi-content-copy</v-icon> Copy
          </v-btn>
          <v-btn depressed color="primary" @click="detailsDialog=false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :timeout="3000" top right>
      {{ snackbar.text }}
      <v-btn text @click="snackbar.show=false">Close</v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import axios from "axios";

export default {
  name: "AdminLogsInfinite",
  data: () => ({
    items: [],
    page: 0,
    size: 30,
    total: 0,
    loading: false,      // initial load
    loadingMore: false,  // subsequent loads
    noMore: false,
    refreshing: false,
    io: null,            // IntersectionObserver
    detailsDialog: false,
    details: null,
    snackbar: { show: false, text: "" },
  }),
  created() {
    this.loadFirstPage();
  },
  mounted() {
    this.mountObserver();
  },
  beforeDestroy() {
    if (this.io) this.io.disconnect();
  },
  methods: {
    async loadFirstPage() {
      this.loading = true;
      this.items = [];
      this.page = 0;
      this.noMore = false;
      try {
        const data = await this.fetchPage(this.page, this.size);
        this.items = data.content || [];
        this.total = data.totalElements || 0;
        this.page++;
        if (data.last || !data.content?.length) this.noMore = true;
      } catch (e) {
        console.error(e);
        this.toast("Failed to load logs");
      } finally {
        this.loading = false;
      }
    },
    async loadNextPage() {
      if (this.loading || this.loadingMore || this.noMore) return;
      this.loadingMore = true;
      try {
        const data = await this.fetchPage(this.page, this.size);
        const next = data.content || [];
        if (next.length) {
          const seen = new Set(this.items.map(i => i.id));
          const merged = next.filter(i => !seen.has(i.id));
          this.items = this.items.concat(merged);
        }
        this.page++;
        if (data.last || next.length === 0) this.noMore = true;
      } catch (e) {
        console.error(e);
        this.toast("Failed to load more");
      } finally {
        this.loadingMore = false;
      }
    },
    async refresh() {
      this.refreshing = true;
      await this.loadFirstPage();
      this.refreshing = false;
    },
    async fetchPage(page, size) {
      const params = { page, size, sort: "createdAt,desc" };
      const { data } = await axios.get("/logs", { params });
      return data;
    },
    mountObserver() {
      const sentinel = this.$refs.sentinel;
      if (!sentinel) return;

      // Observe using the viewport (root=null) so the WHOLE PAGE scroll drives loading
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadNextPage();
        });
      }, { root: null, rootMargin: "0px 0px 400px 0px", threshold: 0.01 });

      this.io.observe(sentinel);
    },
    // UI helpers
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      const p2 = n => (n < 10 ? "0" + n : "" + n);
      return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
    },
    truncate(s, n) {
      return s && s.length > n ? s.slice(0, n) + "…" : s || "—";
    },
    levelClass(l) {
      return {
        "chip-info": l === "INFO" || !l,
        "chip-warn": l === "WARN",
        "chip-error": l === "ERROR",
      };
    },
    levelIcon(l) {
      if (l === "ERROR") return "mdi-alert-circle-outline";
      if (l === "WARN") return "mdi-alert-outline";
      return "mdi-information-outline";
    },
    openDetails(item) {
      this.details = item;
      this.detailsDialog = true;
    },
    copy(text) {
      if (!text) return;
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.toast("Copied to clipboard");
    },
    toast(msg) {
      this.snackbar.text = msg;
      this.snackbar.show = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.logs-infinite { padding: 16px; }

.topbar {
  display: grid; grid-template-columns: 1fr auto; grid-gap: 12px; align-items: center; margin-bottom: 12px;
  .title { font-size: 20px; font-weight: 700; }
  .subtitle { font-size: 13px; opacity: .7; }
}

.list { /* just a flow container; the document scrolls */
}

.log-row {
  display: grid;
  grid-template-columns: 180px 1fr auto;
  grid-gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #eef0f4;
  cursor: pointer;
  transition: background .15s ease;
  border-radius: 8px;
}
.log-row:hover { background: #f8f9fc; }

.time { color: #475467; font-size: 12px; }
.from { font-weight: 600; margin-bottom: 4px; }
.msg { color: #111827; white-space: pre-wrap; }

.meta { display: flex; align-items: flex-start; }

.level-chip { text-transform: none; letter-spacing: 0; }
.chip-info  { color: #155EEF !important; border-color: #155EEF !important; }
.chip-warn  { color: #B54708 !important; border-color: #F79009 !important; }
.chip-error { color: #B42318 !important; border-color: #F04438 !important; }

.loading-more, .end-marker, .empty {
  display: flex; align-items: center; justify-content: center;
  padding: 16px; color: #667085;
}
.empty { flex-direction: column; gap: 6px; }

.sentinel { height: 1px; }

/* smooth appearance for newly appended rows */
.fade-slide-enter-active {
  transition: all .35s cubic-bezier(.2,.8,.2,1);
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(8px);
}

/* Details dialog */
.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  .label { display: block; font-size: 12px; opacity: .7; }
  .value { display: block; font-size: 14px; }
  .wide { grid-column: 1 / -1; }
  .code {
    background: #0b1024; color: #e6e9ff; border-radius: 10px; padding: 12px; overflow: auto; max-height: 420px;
  }
}

.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
</style>
